import {
  Block,
  BlockMilestone,
  BlockRelationship,
  Branch,
  CommonBRsCompoundIndexes,
  CommonBlocksCompoundIndexes,
  ComponentBlock,
  InstanceBlock,
  ListView,
  RootBlock,
  WorkflowBlock,
  blockMilestoneBaseOps,
  isBranch,
  isVirtualizedInstanceChildTargetBR,
  workflowRunBaseOps,
} from '@basehub/data/replicache/index'
import { TinyQueue } from '@basehub/utils/tinyqueue'
import { invariant } from '@epic-web/invariant'
import {
  getMilestoneId,
  flattenContributors,
} from '@basehub/data/replicache/schema/block-milestone/util'
import { SpaceLineage } from '@basehub/data/db/get-repo-lineage'
import { type SQL, sql } from 'drizzle-orm'
import type { ReadTransaction, ReadonlyJSONObject } from 'replicache'
import type { BaseEntryQueryWithDiffParams } from './do'
import { Entry } from '../db/schema'
import { filter } from '../db/list-helpers/filter'
import { beginPerfReport } from '@basehub/utils/perf/index'
import { Executor, ResolvedRef, findEntry } from '../db'
import { SortValue, compare, getSortValues } from '../db/list-helpers/sort'
import { Filter } from './schema/block/search'
import { PromiseOrValue, TreeManager } from '@basehub/utils/tree/index'
import pMap from 'p-map'
import { getBlockFromColumnId } from './schema/block/list/utils'
import { IndexedMap } from '@basehub/utils/indexed-map/index'
import { DiffBetweenTrees } from './diff-helpers'
import { matchesSearch } from './helpers/search'
import {
  InstanceSearchQueueItem,
  compareInstanceSearchQueueItem,
} from './schema/block/reference/util'
import { hash } from '@basehub/utils/hash'

/* -------------------------------------------------------------------------------------------------
 * Helpers
 * -----------------------------------------------------------------------------------------------*/

// Define the base getter function type
type ClientGetter<P, R = ReadonlyJSONObject | null> = (
  tx: ReadTransaction,
  params: P,
  queryKeyHash: string
) => Promise<R>

// Create a syncer function that infers all types from the client implementation
export function createSyncer<TClient extends ClientGetter<any, any>>(config: {
  pullType: 'repo' | 'repo:root' | 'team'
  client: TClient
  syncer: (params: {
    executor: Executor
    brsIndexedMap:
      | IndexedMap<BlockRelationship, CommonBRsCompoundIndexes>
      | undefined
      | null
    blocksIndexedMap: IndexedMap<Block> | undefined | null
    getTreeManager: (
      rootId: string
    ) => Promise<TreeManager<Block, CommonBlocksCompoundIndexes>>
    getBrsByRootBlock: (
      rootBlock: RootBlock
    ) => IndexedMap<BlockRelationship, CommonBRsCompoundIndexes>
    getChangesTreesData: (d: {
      treeNewRootBlockId: string
      treeOldRootBlockId: string | undefined | null
      treeCommonAncestorRootBlockId: string | undefined | null
      diffType: 'commit' | 'merge'
    }) => Promise<{
      treeNew: TreeManager<Block, CommonBlocksCompoundIndexes>
      treeOld: TreeManager<Block, CommonBlocksCompoundIndexes> | null
      treeCommonAncestor: TreeManager<Block, CommonBlocksCompoundIndexes> | null
      diff: DiffBetweenTrees
      sourceBlockIds: string[]
    }>
    sql: typeof sql
    resolvedRef: ResolvedRef | null
    cvrKey: string | null | undefined
    cvrSyncSequence: number | null | undefined
    spaceId: string
    spaceLineage: SpaceLineage
    baseEntryQueryWithDiff: (p: BaseEntryQueryWithDiffParams) => SQL<unknown>
    clientSubscriptions: Array<
      TClient extends ClientGetter<infer P, any>
        ? P & { __queryKeyHash: string }
        : never
    >
  }) => PromiseOrValue<SQL | SQL[] | Entry[] | null>
}) {
  return config
}

export type SyncerSubsMapItem = {
  syncer: string
  params: any
}

export type SQLQueryWithMaxParams = ReturnType<typeof sqlQueryWithMaxParams>

export const sqlQueryWithMaxParams = ({
  max,
  getInitial,
  getSeparator,
  getClosing,
}: {
  max: number
  getInitial: () => SQL<unknown>
  getSeparator: () => SQL<unknown>
  getClosing: () => SQL<unknown>
}) => {
  const all: SQL<unknown>[] = []
  let current: SQL<unknown> = getInitial()
  let currentLength = 0

  return {
    append: (sql: SQL<unknown>) => {
      if (currentLength >= max) {
        current.append(getClosing())
        all.push(current)
        current = getInitial()
        currentLength = 0
      }
      if (currentLength !== 0) {
        current.append(getSeparator())
      }
      current.append(sql)
      currentLength++
    },
    submit: () => {
      if (currentLength === 0 && all.length === 0) return []
      if (currentLength !== 0) {
        current.append(getClosing())
        all.push(current)
      }
      return all
    },
  }
}

export class ExpectedSyncerError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ExpectedSyncerError'
  }
}

/**
 * Builds getInitial/getClosing SQL fragments for a space-prioritized entry
 * query. Uses DISTINCT ON to pick the highest-priority space version for each
 * key BEFORE the CVR version check. This prevents entries from ancestor spaces
 * (with potentially higher version numbers) from leaking through and poisoning
 * CVR records — which would permanently block the current space's entry from
 * being sent to the client.
 */
function spacePrioritizedEntryQuery({
  spaceLineage,
  cvrKey,
  cvrSyncSequence,
  includeDeletedAtCheck,
}: {
  spaceLineage: SpaceLineage
  cvrKey: string | null | undefined
  cvrSyncSequence: number | null | undefined
  includeDeletedAtCheck: boolean
}) {
  const spacesArray = sql.join(
    spaceLineage.map((s) => sql`${s}`),
    sql`,`
  )

  return {
    getInitial: () =>
      sql`SELECT d.key, d.value, d.version, d.spaceid, d.lastmodified FROM (
  SELECT DISTINCT ON (e.key) e.key, e.value, e.version, e.spaceid, e.lastmodified
  FROM entry e
  JOIN (SELECT unnest(ARRAY[${spacesArray}]) AS spaceid, generate_series(1, ${spaceLineage.length}) AS priority) sp ON e.spaceid = sp.spaceid
  WHERE e.key IN (`,
    getClosing: () =>
      sql`) ORDER BY e.key, sp.priority) d
${
  cvrKey
    ? includeDeletedAtCheck
      ? sql`LEFT JOIN cvr_normie c ON c.key = ${cvrKey} AND c.entrykey = d.key
WHERE ((((d.value->>'deletedAt' IS NULL) OR (c.version IS NOT NULL)) AND (c.version IS NULL OR d.version > c.version)) OR (c.syncsequence > ${cvrSyncSequence}))`
      : sql`LEFT JOIN cvr_normie c ON c.key = ${cvrKey} AND c.entrykey = d.key
WHERE ((c.version IS NULL OR d.version > c.version) OR (c.syncsequence > ${cvrSyncSequence}))`
    : sql``
}`,
  }
}

/* -------------------------------------------------------------------------------------------------
 * Workflow Runs
 * -----------------------------------------------------------------------------------------------*/

const workflowRunsSyncer = createSyncer({
  pullType: 'repo:root',
  client: async (
    tx,
    params: { workflowBlock: WorkflowBlock; limit: number; branchId: string }
  ) => {
    const all = await workflowRunBaseOps.list(tx)
    const filtered = all.filter(
      (wr) =>
        (params.workflowBlock.id === wr.workflowBlockId ||
          (params.workflowBlock.sourceBlockId &&
            wr.workflowBlockId === params.workflowBlock.sourceBlockId)) &&
        wr.branchId === params.branchId
    )
    const sorted = filtered.sort((a, b) => {
      const dateToCompare = b.updatedAt ?? b.createdAt
      const otherDate = a.updatedAt ?? a.createdAt
      return dateToCompare.localeCompare(otherDate)
    })
    const paginated = sorted.slice(0, params.limit)
    return paginated
  },
  syncer: (params) => {
    const queries = params.clientSubscriptions
      .map((sub) => {
        const workflowBlockId = sub.workflowBlock?.id
        const sourceBlockId = sub.workflowBlock?.sourceBlockId

        if (!workflowBlockId) return null
        const baseQuery = params.baseEntryQueryWithDiff({
          type: 'single',
          value: sql`'workflow-run'`,
        })

        const sqlCondition = sourceBlockId
          ? params.sql`AND (
            (e.value->>'workflowBlockId' = ${workflowBlockId})
            OR
            (e.value->>'workflowBlockId' = ${sourceBlockId})
          )`
          : params.sql`AND (e.value->>'workflowBlockId' = ${workflowBlockId})`

        return baseQuery.append(
          params.sql`${sqlCondition}
  AND e.value->>'branchId' = ${sub.branchId}
  ORDER BY
    CASE
      WHEN e.value->>'updatedAt' IS NOT NULL AND e.value->>'updatedAt' != 'null'
      THEN (e.value->>'updatedAt')::timestamp
      ELSE (e.value->>'createdAt')::timestamp
    END DESC LIMIT ${sub.limit}`
        )
      })
      .filter(Boolean)

    return queries
  },
})

const workflowRunsByIdSyncer = createSyncer({
  pullType: 'repo:root',
  client: async (
    tx,
    params: { ids: string[]; limit: number; branchId: string }
  ) => {
    const all = await workflowRunBaseOps.list(tx)
    const filtered = all.filter(
      (wr) => params.ids.includes(wr.id) && wr.branchId === params.branchId
    )
    const sorted = filtered.sort((a, b) => {
      const dateToCompare = b.updatedAt ?? b.createdAt
      const otherDate = a.updatedAt ?? a.createdAt
      return dateToCompare.localeCompare(otherDate)
    })
    const paginated = sorted.slice(0, params.limit)
    return paginated
  },
  syncer: (params) => {
    const queries = params.clientSubscriptions.map((sub) => {
      const joinedIds = params.sql.join(
        sub.ids.map((id) => params.sql`${id}`),
        params.sql`,`
      )
      return params.baseEntryQueryWithDiff({
        type: 'single',
        value: sql`'workflow-run'`,
      })
        .append(params.sql`AND e.value->>'id' = ANY (ARRAY[${joinedIds}]::text[])
AND e.value->>'branchId' = ${sub.branchId}
ORDER BY
  CASE
    WHEN e.value->>'updatedAt' IS NOT NULL AND e.value->>'updatedAt' != 'null'
    THEN (e.value->>'updatedAt')::timestamp
    ELSE (e.value->>'createdAt')::timestamp
  END DESC LIMIT ${sub.limit}`)
    })
    return queries
  },
})

const workflowAutofillRunsByBlockIdSyncer = createSyncer({
  pullType: 'repo:root',
  client: async (
    tx,
    params: { blockId: string; limit: number; branchId: string }
  ) => {
    const all = await workflowRunBaseOps.list(tx)
    const filtered = all.filter(
      (wr) =>
        wr.action.type === 'ai-autofill' &&
        wr.action.blockId === params.blockId &&
        wr.branchId === params.branchId
    )
    const sorted = filtered.sort((a, b) => {
      const dateToCompare = b.updatedAt ?? b.createdAt
      const otherDate = a.updatedAt ?? a.createdAt
      return dateToCompare.localeCompare(otherDate)
    })
    const paginated = sorted.slice(0, params.limit)
    return paginated
  },
  syncer: (params) => {
    const queries = params.clientSubscriptions.map((sub) => {
      return params.baseEntryQueryWithDiff({
        type: 'single',
        value: sql`'workflow-run'`,
      }).append(params.sql`
AND e.value->'action'->>'type' = 'ai-autofill'
AND e.value->'action'->>'blockId' = ${sub.blockId}
AND e.value->>'branchId' = ${sub.branchId}
ORDER BY
  CASE
    WHEN e.value->>'updatedAt' IS NOT NULL AND e.value->>'updatedAt' != 'null'
    THEN (e.value->>'updatedAt')::timestamp
    ELSE (e.value->>'createdAt')::timestamp
  END DESC LIMIT ${sub.limit}`)
    })
    return queries
  },
})

/* -------------------------------------------------------------------------------------------------
 * Load Block (used for progressive loading)
 * -----------------------------------------------------------------------------------------------*/

function addLoadedBlocksRecursive({
  brsIndexedMap,
  treeManager,
  sourceBlockId,
  recursive,
  recipient,
  requireBlock = true,
}: {
  brsIndexedMap: IndexedMap<BlockRelationship, CommonBRsCompoundIndexes>
  treeManager: TreeManager<Block, CommonBlocksCompoundIndexes>
  sourceBlockId: string
  recursive: boolean
  recipient: { brs: Set<string>; blocks: Set<string> }
  requireBlock?: boolean
  debug?: boolean
}) {
  const br = brsIndexedMap.get(sourceBlockId)
  if (!br) return
  const block = treeManager.getNode(br.blockId)?.value
  if (requireBlock && !block) return
  if (recipient.brs.has(br.id)) return // already processed, prevent infinite loops
  if (block?.type === 'root') return // root blocks should never be loaded by a syncer

  if (!recursive && block) {
    // we need parents so that requested children get added to the tree
    treeManager.getPath(block.id, { format: 'array' })?.forEach((ancestor) => {
      addLoadedBlocksRecursive({
        brsIndexedMap,
        treeManager,
        sourceBlockId: ancestor,
        recursive: true,
        recipient,
        requireBlock,
      })
    })
  }

  if (!isVirtualizedInstanceChildTargetBR(br)) {
    recipient.brs.add(br.id)
    recipient.blocks.add(br.blockId)
  }

  if (!block) return
  if (block.type === 'list' || block.type === 'reference') {
    return
  }

  const childrenKeys = treeManager.getChildrenKeys(block.id)
  childrenKeys.forEach((id) => {
    addLoadedBlocksRecursive({
      brsIndexedMap,
      treeManager,
      sourceBlockId: id,
      recursive: true,
      recipient,
      requireBlock,
    })
  })
}

export type LoadBlocksParamsMeta = {
  id: string
  done: true
}

const loadBlockSyncer = createSyncer({
  pullType: 'repo:root',
  client: async (
    tx,
    _params: { rootBlockId: string; sourceBlockIds: string[] },
    queryKeyHash
  ) => {
    const meta =
      (await tx.get<LoadBlocksParamsMeta>(
        `custom-syncer-meta/${queryKeyHash}`
      )) ?? null
    return meta
  },
  syncer: async (params) => {
    const paginators: SQL<unknown>[] = []
    const byRootBlockId = new Map<string, Set<string>>()
    params.clientSubscriptions.forEach((sub) => {
      const existing = byRootBlockId.get(sub.rootBlockId)
      if (existing) {
        sub.sourceBlockIds.forEach((sourceBlockId) => {
          existing.add(sourceBlockId)
        })
      } else {
        byRootBlockId.set(sub.rootBlockId, new Set(sub.sourceBlockIds))
      }

      paginators.push(params.sql`SELECT
        'custom-syncer-meta/' || ${sub.__queryKeyHash} AS key,
        ${JSON.stringify({ id: sub.__queryKeyHash, done: true } satisfies LoadBlocksParamsMeta)}::jsonb AS value,
        ${Math.floor(Date.now() / 1000)} AS version,
        (${params.spaceId}) AS spaceid,
        CURRENT_TIMESTAMP AS lastmodified`)
    })

    const brs = new Set<string>()
    const blocks = new Set<string>()
    await pMap(byRootBlockId, async ([rootBlockId, sourceBlockIds]) => {
      const treeManager = await params.getTreeManager(rootBlockId)
      const brsIndexedMap = params.getBrsByRootBlock(
        treeManager.root.value as RootBlock
      )
      invariant(brsIndexedMap, `brsIndexedMap not found`)

      sourceBlockIds.forEach((id) =>
        addLoadedBlocksRecursive({
          treeManager,
          brsIndexedMap,
          sourceBlockId: id,
          recursive: false,
          recipient: { brs, blocks },
        })
      )
    })

    const spEntryQuery = spacePrioritizedEntryQuery({
      spaceLineage: params.spaceLineage,
      cvrKey: params.cvrKey,
      cvrSyncSequence: params.cvrSyncSequence,
      includeDeletedAtCheck: true,
    })

    const qBRsMain = sqlQueryWithMaxParams({
      max: 120,
      ...spEntryQuery,
      getSeparator: () => sql`,`,
    })

    const qBlocksMain = sqlQueryWithMaxParams({
      max: 120,
      ...spEntryQuery,
      getSeparator: () => sql`,`,
    })

    for (const brId of brs) {
      qBRsMain.append(sql`${`br/${brId}`}`)
    }
    for (const blockId of blocks) {
      qBlocksMain.append(sql`${`block/${blockId}`}`)
    }

    return [qBRsMain.submit(), qBlocksMain.submit(), ...paginators].flat()
  },
})

/* -------------------------------------------------------------------------------------------------
 * List View
 * -----------------------------------------------------------------------------------------------*/

export type ListViewParamsMeta = {
  id: string
  total: number
  totalReturned: number
  done: boolean
  createdAt: string
}

type ListViewSyncerParams = {
  sourceBlockId: string
  page: number
  pageSize: number
  selectedView: ListView
  rootBlockId: string
}

const loadListViewSyncer = createSyncer({
  pullType: 'repo:root',
  client: async (tx, _params: ListViewSyncerParams, queryKeyHash) => {
    const meta =
      (await tx.get<ListViewParamsMeta>(
        `custom-syncer-meta/${queryKeyHash}`
      )) ?? null
    if (!meta) throw new ExpectedSyncerError('not ready') // important to throw so that keepPreviousData works as expected in the client
    return meta
  },
  syncer: async (params) => {
    const brs = new Set<string>()
    const blocks = new Set<string>()

    const paginators: SQL<unknown>[] = []

    await pMap(params.clientSubscriptions, async (p) => {
      const treeManager = await params.getTreeManager(p.rootBlockId)
      const brsIndexedMap = params.getBrsByRootBlock(
        treeManager.root.value as RootBlock
      )
      invariant(brsIndexedMap, `brsIndexedMap not found`)

      const metaForPagination: ListViewParamsMeta = {
        createdAt: new Date().toISOString(),
        done: false,
        id: p.__queryKeyHash,
        total: 0,
        totalReturned: 0,
      }

      const perfReport = beginPerfReport(`view ${p.selectedView.id}`, {
        logOnCheckpoint: false,
      })

      const limit = p.pageSize * p.page

      const _getBlock = (id: string) => {
        const node = treeManager.getNode(id, { required: true })
        return node.value
      }
      const _getChildrenLength = (id: string) => {
        return treeManager.getChildrenKeys(id).length
      }

      type QueueItem = {
        sourceBlockId: string
        brId: string
        blockId: string
        sortValues: SortValue[]
      }

      const compareQueueItem = (a: QueueItem, b: QueueItem) => {
        return -compare(a.sortValues, b.sortValues)
      }

      const passesFilter = ({
        block,
        f,
      }: {
        block: ComponentBlock | InstanceBlock
        f: Filter
      }): boolean => {
        if (f.type === 'and') {
          return f.filters.every((f) => passesFilter({ block, f }))
        }
        if (f.type === 'or') {
          return f.filters.some((f) => passesFilter({ block, f }))
        }

        if (f.by === '__title') {
          return filter({
            block: { type: 'text', value: block.title },
            filter: f,
            getBlock: _getBlock,
            getChildrenLength: _getChildrenLength,
          })
        }

        if (f.by === '__hidden') {
          return filter({
            block: { type: 'boolean', value: block.hidden ?? false },
            filter: f,
            getBlock: _getBlock,
            getChildrenLength: _getChildrenLength,
          })
        }

        const { block: filterBlock } = getBlockFromColumnId(f.by, {
          row: block,
          treeManager: treeManager,
        })

        if (!filterBlock) return false

        return filter({
          block: filterBlock,
          filter: f,
          getBlock: _getBlock,
          getChildrenLength: _getChildrenLength,
        })
      }

      const queue = new TinyQueue<QueueItem>([], compareQueueItem)

      for (const row of treeManager.getChildrenKeys(p.sourceBlockId)) {
        const node = treeManager.getNode(row)
        if (!node) continue
        const block = node.value
        if (!block) continue
        if (block.type !== 'instance' && block.type !== 'component') continue // invalid type
        if (block.type === 'component' && block.hidden) continue // template comp

        const passes = p.selectedView.filter.every((f) => {
          return passesFilter({ block, f })
        })
        if (!passes) continue

        metaForPagination.total = metaForPagination.total + 1

        const sortValues = getSortValues({
          brOrder: node.order,
          block,
          sort: p.selectedView.sort,
          getBlockByInstanceChildTarget: (instanceChildTarget) => {
            for (const childId of treeManager.getChildrenKeys(block.id)) {
              const child = treeManager.getNode(childId)?.value
              if (child?.instanceChildTarget === instanceChildTarget) {
                return child
              }
            }
            return null
          },
        })

        const br = brsIndexedMap.get(row)
        if (!br) continue

        const newItem: QueueItem = {
          sourceBlockId: block.sourceBlockId || block.id,
          brId: br.id,
          blockId: block.id,
          sortValues,
        }
        if (queue.size < limit) {
          queue.push(newItem)
        } else {
          const last = queue.peek()
          if (!last || compareQueueItem(newItem, last) > 0) {
            queue.pop()
            queue.push(newItem)
          }
        }
      }

      perfReport.checkpoint('built queue')

      for (const { sourceBlockId } of queue.data) {
        addLoadedBlocksRecursive({
          brsIndexedMap,
          treeManager,
          sourceBlockId,
          recipient: { blocks, brs },
          recursive: false,
        })

        metaForPagination.totalReturned = metaForPagination.totalReturned + 1
      }

      perfReport.checkpoint('build sql')
      perfReport.end()

      paginators.push(params.sql`SELECT
        'custom-syncer-meta/' || ${metaForPagination.id} AS key,
        ${JSON.stringify(metaForPagination)}::jsonb AS value,
        ${Math.floor(Date.now() / 1000)} AS version,
        (${params.spaceId}) AS spaceid,
        CURRENT_TIMESTAMP AS lastmodified`)
    })

    const spEntryQuery = spacePrioritizedEntryQuery({
      spaceLineage: params.spaceLineage,
      cvrKey: params.cvrKey,
      cvrSyncSequence: params.cvrSyncSequence,
      includeDeletedAtCheck: true,
    })

    const qBRsMain = sqlQueryWithMaxParams({
      max: 120,
      ...spEntryQuery,
      getSeparator: () => sql`,`,
    })

    const qBlocksMain = sqlQueryWithMaxParams({
      max: 120,
      ...spEntryQuery,
      getSeparator: () => sql`,`,
    })

    for (const brId of brs) {
      qBRsMain.append(sql`${`br/${brId}`}`)
    }
    for (const blockId of blocks) {
      qBlocksMain.append(sql`${`block/${blockId}`}`)
    }

    return [qBRsMain.submit(), qBlocksMain.submit(), ...paginators].flat()
  },
})

/* -------------------------------------------------------------------------------------------------
 * Load References
 * -----------------------------------------------------------------------------------------------*/

export type LoadReferencesSyncerParamsMeta = {
  id: string
  done: boolean
  createdAt: string
}

type LoadReferencesSyncerParams = {
  sourceBlockId: string
  targetBlockIds: string[]
  rootBlockId: string
}

const loadReferencesSyncer = createSyncer({
  pullType: 'repo:root',
  client: async (tx, _params: LoadReferencesSyncerParams, queryKeyHash) => {
    const meta =
      (await tx.get<LoadReferencesSyncerParamsMeta>(
        `custom-syncer-meta/${queryKeyHash}`
      )) ?? null
    if (!meta) throw new ExpectedSyncerError('not ready') // important to throw so that keepPreviousData works as expected in the client
    return meta
  },
  syncer: async (params) => {
    const brs = new Set<string>()
    const blocks = new Set<string>()

    const paginators: SQL<unknown>[] = []

    await pMap(params.clientSubscriptions, async (p) => {
      const treeManager = await params.getTreeManager(p.rootBlockId)
      const brsIndexedMap = params.getBrsByRootBlock(
        treeManager.root.value as RootBlock
      )
      invariant(brsIndexedMap, `brsIndexedMap not found`)

      const referenceNode = treeManager.getNode(p.sourceBlockId)
      if (!referenceNode || referenceNode.value.type !== 'reference') {
        return
      }

      referenceNode.value.value.targetBlockIds.forEach((targetBlockId) => {
        addLoadedBlocksRecursive({
          brsIndexedMap,
          treeManager,
          sourceBlockId: targetBlockId,
          recipient: { blocks, brs },
          recursive: false,
        })
      })

      const metaForPagination = {
        id: p.__queryKeyHash,
        done: true,
        createdAt: new Date().toISOString(),
      } satisfies LoadReferencesSyncerParamsMeta

      paginators.push(params.sql`SELECT
        'custom-syncer-meta/' || ${metaForPagination.id} AS key,
        ${JSON.stringify(metaForPagination)}::jsonb AS value,
        ${Math.floor(Date.now() / 1000)} AS version,
        (${params.spaceId}) AS spaceid,
        CURRENT_TIMESTAMP AS lastmodified`)
    })

    const spEntryQuery = spacePrioritizedEntryQuery({
      spaceLineage: params.spaceLineage,
      cvrKey: params.cvrKey,
      cvrSyncSequence: params.cvrSyncSequence,
      includeDeletedAtCheck: true,
    })

    const qBRsMain = sqlQueryWithMaxParams({
      max: 120,
      ...spEntryQuery,
      getSeparator: () => sql`,`,
    })

    const qBlocksMain = sqlQueryWithMaxParams({
      max: 120,
      ...spEntryQuery,
      getSeparator: () => sql`,`,
    })

    for (const brId of brs) {
      qBRsMain.append(sql`${`br/${brId}`}`)
    }
    for (const blockId of blocks) {
      qBlocksMain.append(sql`${`block/${blockId}`}`)
    }

    return [qBRsMain.submit(), qBlocksMain.submit(), ...paginators].flat()
  },
})

/* -------------------------------------------------------------------------------------------------
 * Search (instance search (reference and cmd+k))
 * -----------------------------------------------------------------------------------------------*/

export type InstanceSearchParamsMeta = {
  total: number
  totalPerComponent: Record<string, number>
}

const debugInstanceSearchSyncer = false

const getInstanceSearchMetaKey = (params: InstanceSearchMetaParams) => {
  const id = hash(
    params.q +
      params.rootBlockId +
      params.componentSourceBlockIds.join(',') +
      !!params.disableCreateNew +
      !!params.disableLinkExisting
  )
  return `custom-syncer-meta/${id}`
}

type InstanceSearchMetaParams = {
  componentSourceBlockIds: string[]
  q: string | null
  window: { from: number; to: number }
  rootBlockId: string
  disableLinkExisting: boolean
  disableCreateNew: boolean
}

const instanceSearchSyncer = createSyncer({
  pullType: 'repo:root',
  client: async (tx, params: InstanceSearchMetaParams) => {
    const paginationMeta = await tx.get<{
      total: number
      totalPerComponent: string
    }>(getInstanceSearchMetaKey(params))
    if (!paginationMeta) return null
    let totalPerComponent
    try {
      totalPerComponent = JSON.parse(paginationMeta?.totalPerComponent ?? '{}')
    } catch {
      totalPerComponent = {}
    }

    return {
      ...paginationMeta,
      totalPerComponent: totalPerComponent as Record<string, number>,
    }
  },
  syncer: async (params) => {
    const blocks = new Set<string>()
    const brs = new Set<string>()
    const paginators: SQL<unknown>[] = []

    await pMap(params.clientSubscriptions, async (p) => {
      const treeManager = await params.getTreeManager(p.rootBlockId)
      const brsIndexedMap = params.getBrsByRootBlock(
        treeManager.root.value as RootBlock
      )
      invariant(brsIndexedMap, `brsIndexedMap not found`)

      const perfReport = beginPerfReport(`view ${p.__queryKeyHash}`, {
        logOnCheckpoint: false,
      })

      const meta: InstanceSearchParamsMeta = {
        total: 0,
        totalPerComponent: {},
      }

      // different sql queries so the results are ordered by the component array order

      const candidates: InstanceSearchQueueItem[] = []

      p.componentSourceBlockIds.forEach(
        (componentSourceBlockId, componentIndex) => {
          if (!p.disableLinkExisting) {
            for (const instanceId of treeManager.compoundIndexes
              .get('componentInstances')
              ?.get(componentSourceBlockId) ?? []) {
              const node = treeManager.getNode(instanceId)
              if (!node) continue
              const block = node.value
              if (!block || block.type !== 'instance' || block.hidden) continue
              let passesFilter = true
              if (p.q) {
                passesFilter = matchesSearch([block.title || 'Untitled'], p.q)
              }

              if (!passesFilter) continue

              const br = brsIndexedMap.get(instanceId)
              if (!br) continue

              meta.total = meta.total + 1
              meta.totalPerComponent[block.typeOptions.targetBlockId] =
                (meta.totalPerComponent[block.typeOptions.targetBlockId] ?? 0) +
                1

              candidates.push({
                brId: br.id,
                blockId: block.id,
                title: block.title,
                sourceBlockId: br.sourceBlockId,
                componentIndex,
              })
            }
          }

          if (!p.disableCreateNew) {
            // Add one for each component type in the meta result
            // This is for the "New (componentName)" option
            meta.total = meta.total + 1
          }

          const node = treeManager.getNode(componentSourceBlockId)
          if (node && node.value.hidden === false) {
            // Add one for each visible component
            meta.total = meta.total + 1
          }
        }
      )

      candidates.sort(compareInstanceSearchQueueItem)
      if (debugInstanceSearchSyncer) {
        console.log(p.window)
        console.log(
          'candidates',
          candidates.map((c) => c.title)
        )
      }

      candidates.slice(p.window.from, p.window.to + 1).forEach((item) => {
        addLoadedBlocksRecursive({
          treeManager,
          brsIndexedMap,
          sourceBlockId: item.sourceBlockId,
          recursive: false,
          recipient: { brs, blocks },
        })

        if (debugInstanceSearchSyncer) {
          console.log('pushing', item.title)
        }
      })

      perfReport.checkpoint('build queue')

      perfReport.end()

      const metaForPagination = {
        id: p.__queryKeyHash,
        total: meta.total,
        totalPerComponent: JSON.stringify(meta.totalPerComponent),
      }

      paginators.push(params.sql`SELECT
        ${getInstanceSearchMetaKey(p)} AS key,
        ${JSON.stringify(metaForPagination)}::jsonb AS value,
        ${Math.floor(Date.now() / 1000)} AS version,
        (${params.spaceId}) AS spaceid,
        CURRENT_TIMESTAMP AS lastmodified`)
    })

    const spEntryQuery = spacePrioritizedEntryQuery({
      spaceLineage: params.spaceLineage,
      cvrKey: params.cvrKey,
      cvrSyncSequence: params.cvrSyncSequence,
      includeDeletedAtCheck: true,
    })

    const qBRsMain = sqlQueryWithMaxParams({
      max: 120,
      ...spEntryQuery,
      getSeparator: () => sql`,`,
    })

    const qBlocksMain = sqlQueryWithMaxParams({
      max: 120,
      ...spEntryQuery,
      getSeparator: () => sql`,`,
    })

    for (const brId of brs) {
      qBRsMain.append(sql`${`br/${brId}`}`)
    }
    for (const blockId of blocks) {
      qBlocksMain.append(sql`${`block/${blockId}`}`)
    }

    return [qBRsMain.submit(), qBlocksMain.submit(), ...paginators].flat()
  },
})

/* -------------------------------------------------------------------------------------------------
 * Milestones
 * -----------------------------------------------------------------------------------------------*/

const blockMilestonesSyncer = createSyncer({
  pullType: 'repo',
  client: async (
    tx,
    params: {
      sourceBlockId: string
      limit: number
      getAll?: boolean
      members?: string[]
      dateRange?: {
        from: string
        to: string
      }
    },
    queryKeyHash
  ) => {
    const filtered: BlockMilestone[] = []
    for await (const m of tx.scan({
      prefix: params.sourceBlockId,
      indexName: 'milestonesBySourceBlockId',
    })) {
      const milestone = m as BlockMilestone
      if (params.dateRange) {
        const milestoneUpdateAt = (
          milestone.updatedAt ?? milestone.createdAt
        ).replace(/T.*$/gm, '')

        if (
          milestoneUpdateAt >= params.dateRange.from &&
          milestoneUpdateAt <= params.dateRange.to
        ) {
          // passes date range
        } else {
          continue
        }
      }
      if (params.members) {
        const flatten = flattenContributors(milestone.contributors)
        if (params.members.some((id) => flatten.includes(id)) === false) {
          continue
        }
      }
      filtered.push(milestone)
    }

    const sorted = filtered.sort((a, b) =>
      b.createdAt.localeCompare(a.createdAt)
    )
    let result = params.getAll
      ? sorted
      : [...sorted.slice(0, params.limit), ...sorted.slice(-2)]
    const paginationMeta = await tx.get<{
      total: number
    }>(`custom-syncer-meta/${queryKeyHash}`)

    // Deduplicate the result array by milestone ID to ensure no duplicates are present.
    // This is necessary because merging the sliced arrays (line 787) can introduce duplicates
    // if the same milestone appears in both the top and bottom slices.
    result = Array.from(new Map(result.map((m) => [m.id, m])).values())

    return {
      result,
      loading: !paginationMeta,
      hasMore: paginationMeta ? paginationMeta.total > result.length : true,
      remaining: paginationMeta ? paginationMeta.total - result.length : 0,
    }
  },
  syncer: async (params) => {
    if (!params.resolvedRef) return null

    const branch = isBranch(params.resolvedRef.value)
      ? params.resolvedRef.value
      : await findEntry<Branch>({
          db: params.executor,
          entryType: 'branch',
          id: params.resolvedRef.value.branchId,
          spaceID: params.spaceId,
        }).then((entry) => {
          invariant(entry, `branch not found`)
          return entry.value
        })

    const spaceLineageJoined = sql.join(
      params.spaceLineage.map((s) => sql`${s}`),
      sql`,`
    )

    const queries = params.clientSubscriptions.map((sub) => {
      const paginatedQuery = params.sql`
      WITH allMilestones AS (
      SELECT e.key, e.value, e.version, e.spaceid, e.lastmodified, e.value->>'deletedAt'
      FROM entry e
      WHERE e.key LIKE 'milestone/%'
      AND e.spaceid in (${spaceLineageJoined})
      AND e.value->>'sourceBlockId' = ${sub.sourceBlockId}
      AND e.value->>'deletedAt' IS NULL
      AND (
        e.value->>'branchId' = ${branch.id}
        ${
          branch.lineage?.length
            ? sql`
          OR ${sql.join(
            branch.lineage.map(
              (l) =>
                sql`(e.value->>'branchId' = ${l.branchId} and e.value->>'createdAt' <= ${l.lastSyncedAt})`
            ),
            sql` OR `
          )}
          `
            : sql``
        }
      )
      ${
        sub.dateRange
          ? sql`
      AND (
        (e.value->>'createdAt') >= ${sub.dateRange?.from}
        AND (e.value->>'createdAt') <= ${sub.dateRange?.to}
      )`
          : sql``
      }
      ORDER BY e.value->>'createdAt' DESC
    ),
    paginated AS (
      SELECT e.key, e.value, e.version, e.spaceid, e.lastmodified
      FROM allMilestones e
      ${sub.getAll ? sql`` : sql`LIMIT ${sub.limit}`}
    ),
    firstTwoAsc AS (
      SELECT e.key, e.value, e.version, e.spaceid, e.lastmodified
      FROM allMilestones e
      ORDER BY e.value->>'createdAt' ASC
      LIMIT 2
    )
    select e.key, e.value, e.version, e.spaceid, e.lastmodified
    from (
      SELECT * FROM paginated
      UNION
      SELECT * FROM firstTwoAsc
    ) e
    LEFT JOIN cvr_normie c ON c.key = ${params.cvrKey || ''} AND c.entrykey = e.key
    WHERE (
      (c.version IS NULL OR e.version > c.version)
      OR 
      (c.syncsequence > ${params.cvrSyncSequence})
    )

    union all
    SELECT
      'custom-syncer-meta/' || ${sub.__queryKeyHash} AS key,
      jsonb_build_object(
        'id', ${sub.__queryKeyHash}::text,
        'total', (SELECT count(*) FROM allMilestones)
      ) AS value,
      ${Math.floor(Date.now() / 1000)} AS version,
      (${params.spaceId}) AS spaceid,
      CURRENT_TIMESTAMP AS lastmodified
    `

      const deletedMilestonesQuery = params.sql`
      WITH deletedMilestones AS (
      SELECT e.key, e.value, e.version, e.spaceid, e.lastmodified, e.value->>'deletedAt'
      FROM entry e
      WHERE e.key LIKE 'milestone/%'
      AND e.spaceid in (${spaceLineageJoined})
      AND e.value->>'sourceBlockId' = ${sub.sourceBlockId}
      AND e.value->>'deletedAt' IS NOT NULL
      AND (
        e.value->>'branchId' = ${branch.id}
        ${
          branch.lineage?.length
            ? sql`
          OR ${sql.join(
            branch.lineage.map(
              (l) =>
                sql`(e.value->>'branchId' = ${l.branchId} and e.value->>'createdAt' <= ${l.lastSyncedAt})`
            ),
            sql` OR `
          )}
          `
            : sql``
        }
      )
      ${
        sub.dateRange
          ? sql`
      AND (
        (e.value->>'createdAt') >= ${sub.dateRange?.from}
        AND (e.value->>'createdAt') <= ${sub.dateRange?.to}
      )`
          : sql``
      }
    )
    select e.key, e.value, e.version, e.spaceid, e.lastmodified
    from deletedMilestones e
    LEFT JOIN cvr_normie c ON c.key = ${params.cvrKey || ''} AND c.entrykey = e.key
    WHERE (
      (
        (c.version IS NOT NULL)
        AND 
        (e.version > c.version)
      )
      OR 
      (c.syncsequence > ${params.cvrSyncSequence})
    )`
      return [paginatedQuery, deletedMilestonesQuery]
    })
    return queries.flat()
  },
})

const multiBlockMilestonesSyncer = createSyncer({
  pullType: 'repo',
  client: async (tx, params: { rootId: string; sourceBlockIds: string[] }) => {
    return (
      await Promise.all(
        params.sourceBlockIds.map(async (sb) => {
          const milestoneId = getMilestoneId({
            sourceBlockId: sb,
            rootId: params.rootId,
          })
          const milestone = await blockMilestoneBaseOps.get(tx, milestoneId)
          return milestone
        })
      )
    ).filter(Boolean)
  },
  syncer: (params) => {
    const milestoneKeys = params.clientSubscriptions.flatMap((sub) =>
      sub.sourceBlockIds.map(
        (sb) =>
          params.sql`${`milestone/${getMilestoneId({ sourceBlockId: sb, rootId: sub.rootId })}`}`
      )
    )

    if (milestoneKeys.length === 0) return null

    const milestonesSQL = sqlQueryWithMaxParams({
      max: 80,
      getInitial: () => params.sql`${params.baseEntryQueryWithDiff({
        type: 'single',
        value: sql`'milestone'`,
      })}
      AND e.key IN (`,
      getSeparator: () => sql`,`,
      getClosing: () => sql`)`,
    })

    for (const key of milestoneKeys) {
      milestonesSQL.append(key)
    }

    return milestonesSQL.submit()
  },
})

/* -------------------------------------------------------------------------------------------------
 * Diff
 * -----------------------------------------------------------------------------------------------*/

type DiffSyncerPaginationMeta = {
  id: string
  sourceBlockIds: string[]
}

const diffSyncer = createSyncer({
  pullType: 'repo:root',
  client: async (
    tx,
    _params: {
      diffType: 'commit' | 'merge'
      treeNewRootId: string
      treeOldRootId: string | null
      treeCommonAncestorRootId: string | null
    },
    queryKeyHash
  ) => {
    const paginationMeta = await tx.get<DiffSyncerPaginationMeta>(
      `custom-syncer-meta/${queryKeyHash}`
    )
    if (!paginationMeta) return null
    return paginationMeta
  },
  syncer: async (params) => {
    const brs = new Set<string>()
    const blocks = new Set<string>()
    const paginators: SQL<unknown>[] = []

    await pMap(params.clientSubscriptions, async (p) => {
      const perfReport = beginPerfReport(`diff-${p.__queryKeyHash}`, {
        logOnCheckpoint: false,
      })
      const { diff, sourceBlockIds, treeNew, treeOld, treeCommonAncestor } =
        await params.getChangesTreesData({
          treeNewRootBlockId: p.treeNewRootId,
          treeOldRootBlockId: p.treeOldRootId,
          treeCommonAncestorRootBlockId: p.treeCommonAncestorRootId,
          diffType: 'commit',
        })
      const sourceBlockIdsCopy = new Set(sourceBlockIds)

      perfReport.checkpoint('getChangesTreesData')

      const treeNewBrs = params.getBrsByRootBlock(
        treeNew.root.value as RootBlock
      )
      perfReport.checkpoint('treeNewBrs')
      const treeOldBrs = treeOld
        ? params.getBrsByRootBlock(treeOld.root.value as RootBlock)
        : null
      perfReport.checkpoint('treeOldBrs')
      const treeCommonAncestorBrs = treeCommonAncestor
        ? params.getBrsByRootBlock(treeCommonAncestor.root.value as RootBlock)
        : null
      perfReport.checkpoint('treeCommonAncestorBrs')

      diff.items.forEach((item) => {
        sourceBlockIdsCopy.delete(item.value.id)
        if (item.value.type === 'root' || item.value.status === 'unmodified') {
          return
        }

        if (treeOldBrs && treeOld) {
          addLoadedBlocksRecursive({
            sourceBlockId: item.value.id,
            brsIndexedMap: treeOldBrs,
            treeManager: treeOld,
            recursive: false,
            recipient: { brs, blocks },
            requireBlock: false,
          })
        }

        if (treeNewBrs && treeNew) {
          addLoadedBlocksRecursive({
            sourceBlockId: item.value.id,
            brsIndexedMap: treeNewBrs,
            treeManager: treeNew,
            recursive: false,
            recipient: { brs, blocks },
            requireBlock: false,
          })
        }

        if (treeCommonAncestorBrs && treeCommonAncestor) {
          addLoadedBlocksRecursive({
            sourceBlockId: item.value.id,
            brsIndexedMap: treeCommonAncestorBrs,
            treeManager: treeCommonAncestor,
            recursive: false,
            recipient: { brs, blocks },
            requireBlock: false,
          })
        }
      })
      for (const sbDeleted of sourceBlockIdsCopy) {
        // this is probably a deleted block that never got to be committed
        // and is not structural. so,
        // user creates non-structural block -> then deletes it (not committing it) -> next pull() doesn't return the deletion, as the diff doesn't include it
        // here, we kind of force this to happen
        for (const brId of params.brsIndexedMap?.compoundIndexes
          .get('byBlockOrSourceBlockId')
          ?.get(sbDeleted) ?? []) {
          const br = params.brsIndexedMap?.get(brId)
          if (br && br.deletedAt && !br.isCommitted) {
            const block = params.blocksIndexedMap?.get(br?.blockId)
            if (block && block.type === 'root') continue
            brs.add(brId)
            blocks.add(br.blockId)
          }
        }
      }
      perfReport.checkpoint('diff.items')
      perfReport.end()

      paginators.push(params.sql`SELECT
          'custom-syncer-meta/' || ${p.__queryKeyHash} AS key,
          ${JSON.stringify({
            id: p.__queryKeyHash,
            sourceBlockIds,
          } satisfies DiffSyncerPaginationMeta)}::jsonb AS value,
          ${Math.floor(Date.now() / 1000)} AS version,
          (${params.spaceId}) AS spaceid,
          CURRENT_TIMESTAMP AS lastmodified`)
    })

    const spEntryQuery = spacePrioritizedEntryQuery({
      spaceLineage: params.spaceLineage,
      cvrKey: params.cvrKey,
      cvrSyncSequence: params.cvrSyncSequence,
      includeDeletedAtCheck: false,
    })

    const qBRsMain = sqlQueryWithMaxParams({
      max: 120,
      ...spEntryQuery,
      getSeparator: () => sql`,`,
    })

    const qBlocksMain = sqlQueryWithMaxParams({
      max: 120,
      ...spEntryQuery,
      getSeparator: () => sql`,`,
    })

    for (const brId of brs) {
      qBRsMain.append(sql`${`br/${brId}`}`)
    }
    for (const blockId of blocks) {
      qBlocksMain.append(sql`${`block/${blockId}`}`)
    }

    return [qBRsMain.submit(), qBlocksMain.submit(), ...paginators].flat()
  },
})

/* -------------------------------------------------------------------------------------------------
 * All Syncers
 * -----------------------------------------------------------------------------------------------*/

export const syncers = {
  workflowRuns: workflowRunsSyncer,
  workflowAutofillRunsByBlockId: workflowAutofillRunsByBlockIdSyncer,
  workflowRunsById: workflowRunsByIdSyncer,
  loadBlocks: loadBlockSyncer,
  loadListView: loadListViewSyncer,
  blockMilestones: blockMilestonesSyncer,
  multiBlockMilestones: multiBlockMilestonesSyncer,
  instanceSearch: instanceSearchSyncer,
  diff: diffSyncer,
  loadReferences: loadReferencesSyncer,
}

export type Syncers = typeof syncers
